import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Edit2, GripVertical, Check, X, Eye, EyeOff, Youtube } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminVideoManager = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoTitle, setNewVideoTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      const response = await axios.get(`${API}/admin/videos`, { withCredentials: true });
      setVideos(response.data);
    } catch (e) {
      console.error('Failed to load videos:', e);
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const addVideo = async () => {
    if (!newVideoUrl.trim()) {
      toast.error('Please enter a YouTube URL');
      return;
    }

    setAdding(true);
    try {
      const response = await axios.post(`${API}/admin/videos`, {
        url: newVideoUrl,
        title: newVideoTitle || undefined
      }, { withCredentials: true });
      
      setVideos([...videos, response.data.video]);
      setNewVideoUrl('');
      setNewVideoTitle('');
      toast.success('Video added successfully!');
    } catch (e) {
      console.error('Failed to add video:', e);
      toast.error('Failed to add video');
    } finally {
      setAdding(false);
    }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm('Delete this video from the carousel?')) return;

    try {
      await axios.delete(`${API}/admin/videos/${videoId}`, { withCredentials: true });
      setVideos(videos.filter(v => v.id !== videoId));
      toast.success('Video deleted');
    } catch (e) {
      console.error('Failed to delete video:', e);
      toast.error('Failed to delete video');
    }
  };

  const toggleActive = async (video) => {
    try {
      await axios.put(`${API}/admin/videos/${video.id}`, {
        is_active: !video.is_active
      }, { withCredentials: true });
      
      setVideos(videos.map(v => 
        v.id === video.id ? { ...v, is_active: !v.is_active } : v
      ));
      toast.success(video.is_active ? 'Video hidden' : 'Video visible');
    } catch (e) {
      console.error('Failed to toggle video:', e);
      toast.error('Failed to update video');
    }
  };

  const saveTitle = async (videoId) => {
    try {
      await axios.put(`${API}/admin/videos/${videoId}`, {
        title: editTitle
      }, { withCredentials: true });
      
      setVideos(videos.map(v => 
        v.id === videoId ? { ...v, title: editTitle } : v
      ));
      setEditingId(null);
      toast.success('Title updated');
    } catch (e) {
      console.error('Failed to update title:', e);
      toast.error('Failed to update title');
    }
  };

  const extractYouTubeId = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-pink-400 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
            <Youtube className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">Video Carousel</h2>
            <p className="text-sm text-gray-500">Manage landing page videos</p>
          </div>
        </div>
        <span className="text-sm text-gray-400">{videos.length} videos</span>
      </div>

      {/* Add New Video */}
      <div className="bg-gray-50 rounded-xl p-4 mb-6">
        <h3 className="font-medium text-gray-700 mb-3">Add New Video</h3>
        <div className="space-y-3">
          <Input
            placeholder="YouTube URL (e.g., https://youtube.com/watch?v=...)"
            value={newVideoUrl}
            onChange={(e) => setNewVideoUrl(e.target.value)}
            className="w-full"
          />
          <div className="flex space-x-2">
            <Input
              placeholder="Video title (optional)"
              value={newVideoTitle}
              onChange={(e) => setNewVideoTitle(e.target.value)}
              className="flex-1"
            />
            <Button
              onClick={addVideo}
              disabled={adding || !newVideoUrl.trim()}
              className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              {adding ? 'Adding...' : 'Add Video'}
            </Button>
          </div>
        </div>
        
        {/* Preview */}
        {newVideoUrl && extractYouTubeId(newVideoUrl) && (
          <div className="mt-4">
            <p className="text-sm text-gray-500 mb-2">Preview:</p>
            <div className="relative pt-[30%] bg-black rounded-lg overflow-hidden max-w-md">
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src={`https://www.youtube.com/embed/${extractYouTubeId(newVideoUrl)}`}
                title="Preview"
                frameBorder="0"
                allowFullScreen
              />
            </div>
          </div>
        )}
      </div>

      {/* Video List */}
      <div className="space-y-3">
        {videos.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Youtube className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No videos added yet</p>
            <p className="text-sm">Add your first YouTube video above</p>
          </div>
        ) : (
          videos.map((video, idx) => (
            <div
              key={video.id}
              className={`flex items-center space-x-4 p-4 rounded-xl border ${
                video.is_active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100 opacity-60'
              }`}
            >
              {/* Drag Handle */}
              <GripVertical className="w-5 h-5 text-gray-300 cursor-grab" />
              
              {/* Thumbnail */}
              <div className="w-24 h-16 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                <img
                  src={`https://img.youtube.com/vi/${video.youtube_id}/mqdefault.jpg`}
                  alt={video.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                {editingId === video.id ? (
                  <div className="flex items-center space-x-2">
                    <Input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="flex-1"
                      autoFocus
                    />
                    <Button size="sm" variant="ghost" onClick={() => saveTitle(video.id)}>
                      <Check className="w-4 h-4 text-green-600" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                      <X className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <p className="font-medium text-gray-800 truncate">{video.title}</p>
                    <p className="text-sm text-gray-400">Order: {video.order}</p>
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditingId(video.id);
                    setEditTitle(video.title);
                  }}
                  className="text-gray-500 hover:text-blue-600"
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => toggleActive(video)}
                  className={video.is_active ? 'text-green-600' : 'text-gray-400'}
                >
                  {video.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => deleteVideo(video.id)}
                  className="text-gray-500 hover:text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminVideoManager;
